import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";
import { IVisitWithName } from "./components/VisitBooking";

/**
 * Where a candidacy may not be dragged while an appointment is still ahead.
 *
 * Backwards, in other words, plus Not eligible, which is the most common move
 * once a visit is planned. The appointment would otherwise be left behind: it
 * keeps holding a slot nobody else can book, and it stays in the broker's own
 * Google or Outlook calendar, so somebody is expected at a door for a candidacy
 * that has moved on.
 *
 * Forward and archived are deliberately absent. Someone who is becoming a
 * tenant is not someone whose visit should be called off, and archiving is
 * already allowed from everywhere.
 */
export const STEPS_BLOCKED_WHILE_VISIT_AHEAD: EIntakeStep[] = [
  EIntakeStep.candidateVisitor,
  EIntakeStep.waitingList,
  EIntakeStep.planAsVisitor,
  EIntakeStep.invited,
  EIntakeStep.notEligible,
];

/**
 * The candidacies in this drag that still have an appointment coming, by name,
 * without repeating anyone who has more than one.
 *
 * An appointment that already took place blocks nothing: it happened, and there
 * is nothing left to cancel. Whether one is still ahead is read off its end, so
 * a visit under way counts as ahead: it is in the calendar and can still be
 * called off.
 *
 * Cancelled appointments never reach this: they are dropped where the list of
 * visits is built.
 */
export const getNamesBlockingMove = (
  applicationIds: string[],
  visits: IVisitWithName[],
  now: Date,
): string[] => {
  const blocking = new Set<string>();

  applicationIds.forEach(applicationId => {
    const visit = visits.find(
      candidate =>
        candidate.applicationId === applicationId &&
        new Date(candidate.end || candidate.start).getTime() > now.getTime(),
    );

    if (visit) {
      blocking.add(visit.name);
    }
  });

  return Array.from(blocking);
};

/**
 * Whether a tab can take the candidacies being dragged, decided when the drag
 * starts so a tab that would refuse them looks disabled the whole way rather
 * than after the drop (DEV-3222).
 *
 * `allowedSteps` is the flow's own rule for the tab the drag started on;
 * `hasVisitAhead` adds the visit rule on top, the same one that refuses the
 * drop, so the two can never disagree about what lands.
 */
export const isDropStepAllowed = (
  step: EIntakeStep,
  allowedSteps: EIntakeStep[],
  hasVisitAhead: boolean,
): boolean => {
  if (!allowedSteps.includes(step)) {
    return false;
  }

  return !(hasVisitAhead && STEPS_BLOCKED_WHILE_VISIT_AHEAD.includes(step));
};
